// START GENAI@CHATGPT4
import { HandlerEvent } from 'post-robot';
import { ZodIssue } from 'zod';
import { error } from 'common/errors';
import { pushINOData, pushUctpGetCards } from 'common/gamp';
import { asyncMarkWrapper, markEnd, markStart } from 'common/instrumentation';
import completeIdentityValidation from 'orch-communicator/internal/complete-identity-validation';
import identityLookup from 'orch-communicator/internal/identity-lookup';
import { getState, saveApiResponseCache } from 'orch-communicator/store';
import { getMeta, getSupportedCardNetworks } from 'orch-communicator/store/selectors';
import { generateHashedIdentityValue } from 'orch-communicator/utils/generate-hashed-identity';
import { getHashedIdentityFromIdtoken } from 'orch-communicator/utils/get-hashed-identity';
import { formatDate, removeUnwantedProfileProperties } from 'orch-communicator/utils/get-profiles';
import { getSrcEmailMask } from 'orch-communicator/utils/parse-idtoken';
//START GENAI
import selectiveUnbindMastercard from 'orch-communicator/utils/selective-unbind-mastercard';
import selectiveUnbindVisa from 'orch-communicator/utils/selective-unbind-visa';
//END GENAI
import { validateInitResponses } from 'orch-communicator/utils/validate-init-responses';
import { ConsumerIdentity } from 'types/emvco';
import { CompleteIdentityValidationData } from 'types/vsb-sdk/complete-identity-validation';
import { GetCardsInput } from 'types/vsb-sdk/get-cards';
import { IdLookupData, IdentityLookupPayload } from 'types/vsb-sdk/identity-lookup';
import { InitiateIdentityValidationPayload } from 'types/vsb-sdk/initiate-identity-validation';
import initiateIdentityValidation from './initiate-identity-validation';
// END GENAI@CHATGPT4

// START GENAI@CHATGPT4
/**
 * Retrieves the cards for a validated consumer identity.
 * @param validatedConsumerIdentity - The validated consumer identity.
 * @returns A promise that resolves to the retrieved cards or an error.
 */
// END GENAI@CHATGPT4
export default async function getCards(validatedConsumerIdentity: GetCardsInput) {
  if (validateInitResponses()) {
    return error('AUTH_INVALID', 'Invalid federated id token.', ' ' as unknown as ZodIssue[]);
  }

  const idTokens = getState().srcSdks.apiResponseCache.idTokens;

  // START GENAI@CHATGPT4
  const hashedIdentityFromIdToken = getHashedIdentityFromIdtoken(idTokens.idTokens[0]) || null;
  const generatedHasedIdentity = generateHashedIdentityValue(
    validatedConsumerIdentity.consumerIdentity.identityValue
  );

  /*eslint-disable */
  // END GENAI@CHATGPT4

  const getSrcResponses: { [key: string]: object } = {};
  //eslint-disable-next-line
  const vSDK = getState().srcSdks.loadedSdk as { [key: string]: any };
  const cardBrands = getSupportedCardNetworks();

  // START GENAI@CHATGPT4
  const brandsWithConsumerPresent = Object.entries(
    getState().srcSdks.apiResponseCache.identityLookUp
  ) //eslint-disable-next-line
    .filter(([brand, details]) => (details as any).consumerPresent)
    .map(([brand]) => brand);
  // END GENAI@CHATGPT4

  try {
    /**
     * START GENAI@GHCOPILOT
     * If the consumer identity has validation data, call the complete identity validation endpoint.
     * This check must come first to handle the fallback flow correctly.
     * END GENAI@GHCOPILOT
     */
    if ('validationData' in validatedConsumerIdentity) {
      markStart('vsb_merchant_pov_get_cards_civ_gsp');
      let completeIdToken;
      try {
        const completeIdentityValidationEvent: HandlerEvent<CompleteIdentityValidationData> = {
          source: null,
          origin: 'getCards',
          data: {
            validationData: validatedConsumerIdentity.validationData as string
          }
        };

        completeIdToken = await completeIdentityValidation(completeIdentityValidationEvent);
      } catch (e: unknown) {
        markEnd('vsb_merchant_pov_get_cards_civ_gsp', { failure: true, doFlush: true });
        // START GENAI@GHCOPILOT
        let errMsg = 'General Server Error.';

        if (e instanceof Error) {
          errMsg = e.message;
        } else if (e) {
          const err = e as { error: { reason: string } };
          errMsg = err.error.reason;
        } else {
          errMsg = JSON.stringify(e);
        }

        pushUctpGetCards('CIV - error', errMsg);
        // END GENAI@GHCOPILOT
        return e;
      }

      const promises = [];

      for (const brand of brandsWithConsumerPresent) {
        promises.push(
          asyncMarkWrapper(
            'get_src_profile',
            brand,
            vSDK[brand].getSrcProfile({ idTokens: [completeIdToken] })
          )
        );
      }

      const promiseResults = await Promise.allSettled(promises);

      if (!promiseResults.some(result => result.status === 'fulfilled')) {
        const visaPromiseResult = promiseResults[0] as PromiseRejectedResult;

        const e = visaPromiseResult.reason;

        markEnd('vsb_merchant_pov_get_cards_civ_gsp', { failure: true, doFlush: true });
        if ('error' in e && e.error && 'reason' in e.error) {
          if (e.error.reason === 'AUTH_INVALID') {
            return error(
              'AUTH_INVALID',
              'Invalid federated id token.',
              ' ' as unknown as ZodIssue[]
            );
          } else if (e.error.reason === 'INVALID_PARAMETER') {
            return error('INVALID_PARAMETER', '', '' as unknown as ZodIssue[]);
          } else if (e.error.reason === 'ACCT_INACCESSIBLE') {
            return error(
              'ACCT_INACCESSIBLE',
              'The account is inaccessible.',
              ' ' as unknown as ZodIssue[]
            );
          }
        }

        // START GENAI@GHCOPILOT
        pushUctpGetCards('CIV - error', e.error.reason);
        // END GENAI@GHCOPILOT
        return error('SERVER ERROR', 'General Server Error.', ' ' as unknown as ZodIssue[]);
      }

      // START GENAI@CHATGPT4
      promiseResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const cardDescriptor =
            result.value?.profiles?.[0]?.maskedCards?.[0]?.paymentCardDescriptor;

          const hasMaskedCards = result.value?.profiles?.[0]?.maskedCards?.length > 0;

          if (cardDescriptor && cardDescriptor.toUpperCase() === 'MASTERCARD') {
            //eslint-disable-next-line
            getSrcResponses['MASTERCARD'] = result.value;
          } else if (result.value?.profiles?.length > 0 && hasMaskedCards) {
            //eslint-disable-next-line
            getSrcResponses['VISA'] = result.value;
          }
        }
      });
      // END GENAI@CHATGPT4

      pushUctpGetCards('CIV', undefined);
      pushINOData(null, 'VSB getCards CIV', 'vsb_complete_identity_validation_');

      const apiResponseCache = {
        ...getState().srcSdks.apiResponseCache,
        email: validatedConsumerIdentity.consumerIdentity.identityValue,
        getSrcProfile: getSrcResponses
      };

      saveApiResponseCache(apiResponseCache);

      let srcProfiles = promiseResults
        .filter(result => result.status === 'fulfilled')
        //eslint-disable-next-line
        .map(result => (result as PromiseFulfilledResult<any>).value)
        .flatMap(profile => profile.profiles); // Flatten the profiles

      srcProfiles = srcProfiles.map(removeUnwantedProfileProperties);

      const maskedCards = srcProfiles.flatMap(profile => profile.maskedCards);

      const otpProviderNetwork = getMeta().otpProvider;

      let maskedConsumers;

      // START GENAI@CHATGPT4
      if (otpProviderNetwork === 'VISA') {
        let emailValidationChannel = apiResponseCache.initiateIdentityValidation
          .maskedValidationChannel as string;

        const emailRegex = /\b[A-Za-z0-9.*_%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;

        // Search for an email address in the maskedValidationChannel
        const emailMatch = emailValidationChannel.match(emailRegex);

        if (emailMatch) {
          // emailMatch[0] contains the email address
          emailValidationChannel = emailMatch[0];
        }

        maskedConsumers = {
          maskedConsumerIdentity: {
            identityProvider: validatedConsumerIdentity.consumerIdentity.identityProvider,
            identityType: validatedConsumerIdentity.consumerIdentity.identityType,
            maskedIdentityValue: emailValidationChannel
          },
          maskedEmailAddress: emailValidationChannel
        };
      } else if (otpProviderNetwork === 'MASTERCARD') {
        const identityValidation = apiResponseCache.initiateIdentityValidation;
        const emailIdentity = (
          identityValidation.supportedValidationChannels as Array<{
            identityType: string;
            maskedValidationChannel: string;
          }>
        ).find(
          (channel: { identityType: string; maskedValidationChannel: string }) =>
            channel.identityType === 'EMAIL_ADDRESS'
        );

        const emailValidationChannel = emailIdentity ? emailIdentity.maskedValidationChannel : null;
        maskedConsumers = {
          maskedConsumerIdentity: {
            identityProvider: validatedConsumerIdentity.consumerIdentity.identityProvider,
            identityType: validatedConsumerIdentity.consumerIdentity.identityType,
            maskedIdentityValue: emailValidationChannel
          },
          maskedEmailAddress: emailValidationChannel
        };
      }
      // END GENAI@CHATGPT4

      markEnd('vsb_merchant_pov_get_cards_civ_gsp', { doFlush: true });
      return {
        actionCode: 'SUCCESS',
        profiles: [
          {
            maskedCards,
            maskedConsumer: maskedConsumers
          }
        ]
      };
    }

    //  If the idTokens are available then
    if (idTokens.idTokens.length > 0) {
      markStart('vsb_merchant_pov_get_cards_gsp');
      //For recognized flow if the id token is not matching with the hashed identity value, user send different email id
      if (generatedHasedIdentity === hashedIdentityFromIdToken) {
        // START GENAI@CHATGPT4
        const promises = [];

        /* START GENAI@GHCOPILOT */
        for (const brand of cardBrands) {
          promises.push(
            asyncMarkWrapper('rr_get_src_profile', brand, vSDK[brand].getSrcProfile(idTokens))
          );
        }
        /* END GENAI@GHCOPILOT */

        const promiseResults = await Promise.allSettled(promises);

        if (!promiseResults.some(result => result.status === 'fulfilled')) {
          const visaPromiseResult = promiseResults[0] as PromiseRejectedResult;

          const e = visaPromiseResult.reason;

          if ('error' in e && e.error && 'reason' in e.error) {
            if (e.error.reason === 'AUTH_INVALID') {
              return error(
                'AUTH_INVALID',
                'Invalid federated id token.',
                ' ' as unknown as ZodIssue[]
              );
            } else if (e.error.reason === 'INVALID_PARAMETER') {
              return error('INVALID_PARAMETER', '', '' as unknown as ZodIssue[]);
            } else if (e.error.reason === 'ACCT_INACCESSIBLE') {
              return error(
                'ACCT_INACCESSIBLE',
                'The account is inaccessible.',
                ' ' as unknown as ZodIssue[]
              );
            }
          }

          pushUctpGetCards('GSP - error', e.error.reason);

          return error('SERVER ERROR', 'General Server Error.', ' ' as unknown as ZodIssue[]);
        }
        // END GENAI@CHATGPT4

        pushUctpGetCards('GSP', undefined);
        pushINOData(null, 'VSB getCards GSP', 'vsb_get_src_profile_');

        // START GENAI@CHATGPT4
        promiseResults.forEach(result => {
          if (result.status === 'fulfilled') {
            const cardDescriptor =
              result.value?.profiles?.[0]?.maskedCards?.[0]?.paymentCardDescriptor;

            const hasMaskedCards = result.value?.profiles?.[0]?.maskedCards?.length > 0;

            if (cardDescriptor && cardDescriptor.toUpperCase() === 'MASTERCARD') {
              //eslint-disable-next-line
              getSrcResponses['MASTERCARD'] = result.value;
            } else if (result.value?.profiles?.length > 0 && hasMaskedCards) {
              //eslint-disable-next-line
              getSrcResponses['VISA'] = result.value;
            }
          }
        });
        // END GENAI@CHATGPT4

        const apiResponseCache = {
          ...getState().srcSdks.apiResponseCache,
          email: validatedConsumerIdentity.consumerIdentity.identityValue,
          getSrcProfile: getSrcResponses
        };

        saveApiResponseCache(apiResponseCache);

        let srcProfiles = promiseResults
          .filter(result => result.status === 'fulfilled')
          //eslint-disable-next-line
          .map(result => (result as PromiseFulfilledResult<any>).value)
          .flatMap(profile => profile.profiles); // Flatten the profiles

        srcProfiles = srcProfiles.map(removeUnwantedProfileProperties);

        const maskedCards = srcProfiles.flatMap(profile => profile.maskedCards);

        // START GENAI@CHATGPT4
        //eslint-disable-next-line
        maskedCards.sort((a: any, b: any) => {
          const aUsed = a.dateOfCardLastUsed !== undefined && a.dateOfCardLastUsed !== null;
          const bUsed = b.dateOfCardLastUsed !== undefined && b.dateOfCardLastUsed !== null;

          if (aUsed && bUsed) {
            // Both cards are used
            const usageDateComparison =
              formatDate(b.dateOfCardLastUsed).getTime() -
              formatDate(a.dateOfCardLastUsed).getTime();

            /* eslint-disable */
            if (usageDateComparison !== 0) {
              return usageDateComparison; // If the usage dates are different, sort by usage date
            } else {
              return (
                formatDate(a.dateOfCardCreated).getTime() -
                formatDate(b.dateOfCardCreated).getTime()
              ); // If usage dates are same, sort by creation date
            }
          } else if (aUsed) {
            // Only card A is used
            return -1; // Card A should come before card B
          } else if (bUsed) {
            // Only card B is used
            return 1; // Card B should come before card A
          } else {
            // Neither card is used
            return (
              formatDate(a.dateOfCardCreated).getTime() - formatDate(b.dateOfCardCreated).getTime()
            ); // Sort by creation date
          }
          /* eslint-enable */
        });
        // END GENAI@CHATGPT4

        const maskedEmailAddress = getSrcEmailMask(idTokens.idTokens[0]);

        // START GENAI@GHCOPILOT
        const { identityValue, ...rest } = validatedConsumerIdentity.consumerIdentity;
        const maskedConsumerIdentity = {
          ...rest,
          maskedIdentityValue: maskedEmailAddress
        };
        // END GENAI@GHCOPILOT

        const maskedConsumer = {
          maskedConsumerIdentity,
          maskedEmailAddress
        };

        markEnd('vsb_merchant_pov_get_cards_gsp', { doFlush: true });

        /* eslint-disable */
        return {
          actionCode: 'SUCCESS',
          profiles: [
            {
              maskedCards,
              maskedConsumer: maskedConsumer
            }
          ]
        };
        /* eslint-enable */
      }

      //START GENAI
      // Hash mismatch: user entered different email than original recognized user email

      try {
        // Call selective unbind only for Mastercard network as per requirements
        await selectiveUnbindMastercard();
        // START GENAI@CLAUDE
        /*eslint-disable-next-line */
        console.log('Mastercard unbind completed on hash mismatch');

        await selectiveUnbindVisa();
        /*eslint-disable-next-line */
        console.log('VISA unbind completed on hash mismatch');

        pushUctpGetCards(
          'Hash Mismatch - Mastercard & VISA Unbound, Starting Fallback Flow',
          undefined
        );

        // END GENAI@CLAUDE
        // Fallback to full identity validation flow
        markStart('vsb_merchant_pov_get_cards_fallback_ilu');

        // Step 1: Identity Lookup
        let identityLookupResult;
        try {
          const identityLookupEvent: HandlerEvent<IdLookupData> = {
            source: null,
            origin: 'getCards',
            data: {
              consumerIdentity: validatedConsumerIdentity.consumerIdentity as ConsumerIdentity
            }
          };

          identityLookupResult = await identityLookup(identityLookupEvent);
          pushINOData(null, 'VSB getCard Fallback ILU', 'vsb_idLookup_');
        } catch (e: Error | unknown) {
          markEnd('vsb_merchant_pov_get_cards_fallback_ilu', { failure: true, doFlush: true });
          const errMsg = String(e) || 'General Server Error.';
          pushUctpGetCards('Fallback ILU - error', e instanceof Error ? e.message : errMsg);
          return e;
        }

        // Step 2: Check if consumer is present and initiate identity validation
        if (identityLookupResult && 'consumerPresent' in identityLookupResult) {
          if ((identityLookupResult as IdentityLookupPayload).consumerPresent) {
            let maskedChannel;
            try {
              maskedChannel = await initiateIdentityValidation();
              pushINOData(null, 'VSB getCard Fallback IDV', 'vsb_initiate_identity_validation_');
            } catch (e: Error | unknown) {
              markEnd('vsb_merchant_pov_get_cards_fallback_ilu', { failure: true, doFlush: true });
              const errMsg = String(e) || 'General Server Error.';
              pushUctpGetCards('Fallback IDV - error', e instanceof Error ? e.message : errMsg);
              return e;
            }

            markEnd('vsb_merchant_pov_get_cards_fallback_ilu', { doFlush: true });
            pushUctpGetCards('Fallback Flow - ILU+IDV', undefined);

            // Return PENDING_CONSUMER_IDV so consumer can provide validation data
            return {
              actionCode: 'PENDING_CONSUMER_IDV',
              profiles: [],
              maskedValidationChannel: (maskedChannel as InitiateIdentityValidationPayload)
                .maskedValidationChannel
            };
          }
        }

        // Consumer not found
        markEnd('vsb_merchant_pov_get_cards_fallback_ilu', { failure: true, doFlush: true });
        pushUctpGetCards('Fallback ILU - error', 'CONSUMER_NOT_FOUND');
        return error('ADD_CARD', '', ' ' as unknown as ZodIssue[]);
      } catch (e) {
        /*eslint-disable-next-line */
        console.error('Failed to execute fallback flow on hash mismatch:', e);
        markEnd('vsb_merchant_pov_get_cards_gsp', { failure: true, doFlush: true });
        return error('SERVER ERROR', 'Failed to process request.', ' ' as unknown as ZodIssue[]);
      }
      //END GENAI
    }

    let result;
    try {
      /* START GENAI@GHCOPILOT */
      markStart(`vsb_merchant_pov_get_cards_ilu`);
      const identityLookupEvent: HandlerEvent<IdLookupData> = {
        source: null,
        origin: 'getCards',
        data: {
          consumerIdentity: validatedConsumerIdentity.consumerIdentity as ConsumerIdentity
        }
      };

      result = await identityLookup(identityLookupEvent);

      pushINOData(null, 'VSB getCard ILU', 'vsb_idLookup_');
      /* END GENAI@GHCOPILOT */
    } catch (e: Error | unknown) {
      markEnd('vsb_merchant_pov_get_cards_ilu', { failure: true, doFlush: true });
      // START GENAI@GHCOPILOT
      const errMsg = String(e) || 'General Server Error.';
      pushUctpGetCards('ILU - error', e instanceof Error ? e.message : errMsg);
      // END GENAI@GHCOPILOT
      return e;
    }

    if (result && 'consumerPresent' in result) {
      if ((result as IdentityLookupPayload).consumerPresent) {
        let maskedChannel;
        try {
          maskedChannel = await initiateIdentityValidation();
        } catch (e: Error | unknown) {
          markEnd('vsb_merchant_pov_get_cards_ilu', { failure: true, doFlush: true });
          // START GENAI@GHCOPILOT
          const errMsg = String(e) || 'General Server Error.';
          pushUctpGetCards('ILU - error', e instanceof Error ? e.message : errMsg);
          // END GENAI@GHCOPILOT
          return e;
        }

        markEnd('vsb_merchant_pov_get_cards_ilu', { doFlush: true });
        // START GENAI@GHCOPILOT
        pushUctpGetCards('ILU', undefined);
        // END GENAI@GHCOPILOT
        pushINOData(null, 'VSB getCard IDV', 'vsb_initiate_identity_validation_');

        return {
          actionCode: 'PENDING_CONSUMER_IDV',
          profiles: [],
          maskedValidationChannel: (maskedChannel as InitiateIdentityValidationPayload)
            .maskedValidationChannel
        };
      }
    }

    markEnd('vsb_merchant_pov_get_cards_ilu', { failure: true, doFlush: true });
    // START GENAI@GHCOPILOT
    pushUctpGetCards('ILU - error', 'CONSUMER_NOT_FOUND');
    // END GENAI@GHCOPILOT

    return error('ADD_CARD', '', ' ' as unknown as ZodIssue[]);
  } catch (e) {
    return e;
  }
}
// END GENAI@CHATGPT4
